import { Component, HostListener, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import { environment as env } from '../../../environments/environment';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { SseBurstBreaker } from '../shared/sse-burst-breaker';
import { SoundPlayerService } from '../shared/sound-player.service';
import { SoundSettingsDialogComponent } from '../shared/sound-settings-dialog.component';
import { UserPrefsService } from '../../@services/user-prefs.service';
import { CustomerColorsService } from '../../@services/customer-colors.service';

interface ErrorReport {
  _id: string;
  created_at: string;
  error_code: string;
  customer?: string;
  scanner_path?: string;
  url?: string;
  user_agent?: string;
  user_ip?: string;
  x_forwarded_for?: string;
  remote_addr?: string;
  http_status?: number | null;
  f2_response_body?: string;
  f2_response_message?: string;
  extra?: Record<string, any>;
  // Client-only marker used by the live-row highlight: epoch-ms after
  // which the row should NO LONGER carry the freshly-arrived flash.
  // ag-grid rowClassRules reads this on each refresh to clear the class.
  _freshUntil?: number;
}

// Persist the Live preference so a tab refresh doesn't silently flip
// the operator out of realtime mode. Keep the key namespaced.
const LIVE_LOCALSTORAGE_KEY = 'error_reports_live_on';
// Cap the in-memory list so a sustained incident doesn't grow the grid
// without bound. 500 keeps an hour-ish of busy ops visible.
const LIVE_MAX_ROWS = 500;
// Row highlight duration — must match the SCSS animation length so the
// rowClassRules predicate clears at the same time the visual fades.
const LIVE_HIGHLIGHT_MS = 2000;
// Server-side paging window. Raised 100 → 1000 (2026-06-25) so a single
// page covers more historical context without the operator having to
// hit Older / Newer through dozens of pages. Backend cap also raised to
// match (error-reports.service.ts).
const PAGE_SIZE = 1000;

@Component({
  standalone: true,
  selector: 'app-error-reports',
  imports: [AgGridAngular, CommonModule, FormsModule, RouterLink, SoundSettingsDialogComponent],
  templateUrl: './error-reports.component.html',
  styleUrl: './error-reports.component.scss',
})
export class ErrorReportsComponent implements OnInit, OnDestroy {

  // Lookup-by-ID — what the operator pastes from the customer's toast.
  lookupId = '';
  lookupErr: string | null = null;
  loadingLookup = false;

  // Listing
  list: ErrorReport[] = [];
  loadingList = false;
  listErr: string | null = null;
  // Flips true after the first loadList() resolves. Drives the grid
  // *ngIf so ag-grid stays mounted across re-filters / refreshes —
  // unmounting wipes the column filter model (operator types "strat"
  // in Customer, list collapses to 0, grid unmounts, refilter rebuilds
  // grid with empty filter model — Mike caught it 2026-06-26).
  hasInitialized = false;
  // Server-side filter state — every column with a backend equivalent.
  // Driven by ag-grid's getFilterModel() via onGridFilterChanged. The
  // backend builds case-insensitive regex queries so the in-memory
  // 1000-row window doesn't gate matches that live further back in
  // history (Mike asked for "filter columns to reach past the in-memory
  // row limit" 2026-06-25).
  filterErrorCode = '';
  // Set-filter exact-match allowlist for the Error code column. Carried
  // alongside filterErrorCode (text prefix) — backend honors both if
  // both arrive. Mike 2026-06-26.
  filterErrorCodesIn: string[] = [];
  filterCustomer = '';
  filterId = '';
  filterScannerPath = '';
  filterMessage = '';
  filterUserEmail = '';
  filterHttpStatus: number | null = null;
  filterHttpStatusOp: 'eq' | 'lt' | 'gt' | 'lte' | 'gte' | 'ne' = 'eq';
  filterCreatedAfter = '';
  filterCreatedBefore = '';

  // Detail view (either looked-up or clicked from list)
  selected: ErrorReport | null = null;

  // ag-grid plumbing
  theme = agGridThemes.dark;
  grid: any;
  // Selection bookkeeping for the multi-select copy buttons. Updated
  // from the grid's onSelectionChanged event so the button labels can
  // show "(N selected)" live.
  selectedCount = 0;
  // Bound to the Delete button's loading state — disables the button
  // while the POST /admin/error-reports/delete is in flight.
  deletingSelected = false;

  // ── Live mode ────────────────────────────────────────────────────────
  // toggleable from the header. When `liveOn && !liveSuspended` an
  // EventSource is held open against /admin/error-reports/stream and
  // each pushed doc is prepended to the grid.
  liveOn = false;
  // suspended while the operator is paging through history. Banner in
  // the template invites them back to the newest page to resume.
  liveSuspended = false;
  // Paging back-stack — each entry is the `before` cursor that fetched
  // the page above the current one. Empty = page 1 (newest). Pushing
  // a cursor and re-fetching moves "older"; popping moves "newer".
  pageStack: string[] = [];
  // True when the most recent fetch came back with a full page; signals
  // there's an "older" page to navigate to.
  hasOlder = false;

  private _eventSource: EventSource | null = null;
  // Set when we've torn down the EventSource and are waiting to re-mint
  // a fresh token + reopen. Cleared on successful open or on Live=off.
  private _reconnectTimer: any = null;
  // Re-renders the "When" column every minute so formatRelative()'s
  // output ("5m ago" etc.) doesn't go stale while the operator stares
  // at the grid. Aligned to the wall-clock minute boundary so every
  // row's text updates in the same paint.
  private _whenRefreshTimer: any = null;

  private readonly apiBase = `${env.apiUrl}/rest`;

  // ms-since-epoch of last-seen at the moment the operator navigated
  // here. Rows with created_at > this render with the NEW pill +
  // customer-color tint. Captured ONCE in ngOnInit so the highlight
  // doesn't disappear as rows tick in and the live last_seen advances.
  private _arrivalSeenMs: number = 0;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    public soundPlayer: SoundPlayerService,  // public — bound to the mute toggle in the template
    private userPrefs: UserPrefsService,
    private customerColors: CustomerColorsService,
  ) {
    // Two arrival paths to handle:
    //   1) Nav-click via the sidebar — AppComponent already called
    //      beforeMarkSeen + markSeen, snapshot is set to the pre-bump
    //      value. Read it directly.
    //   2) Direct arrival (deep-link, F5, manual URL) — snapshot is
    //      null. Capture the current last_seen as the snapshot ourselves
    //      and only then bump, so the highlight reflects what's new
    //      since the operator last looked.
    const navSnap = this.userPrefs.arrivalSnapshot('error_reports');
    if (navSnap) {
      this._arrivalSeenMs = Date.parse(navSnap);
    } else {
      this.userPrefs.beforeMarkSeen('error_reports');
      const fallback = this.userPrefs.arrivalSnapshot('error_reports');
      this._arrivalSeenMs = fallback ? Date.parse(fallback) : 0;
      this.userPrefs.markSeen('error_reports');
    }
    // Colors are a nice-to-have on first paint, but once the directory
    // list lands we want the tint to apply without waiting for the
    // next refresh — mirror the Users page redraw pattern.
    this.customerColors.hydrate().then(() => {
      this.grid?.api?.redrawRows?.();
    });
    this.init_grid();
  }

  private _isNewSinceArrival(row: any): boolean {
    if (!this._arrivalSeenMs || !row?.created_at) return false;
    const t = Date.parse(String(row.created_at));
    return Number.isFinite(t) && t > this._arrivalSeenMs;
  }

  // Bound from ag-grid getRowStyle. Three layers in one return:
  //   - --row-flash-color CSS var set on every row to that customer's
  //     accent RGB triplet so the 2s live-row-fresh pulse follows the
  //     customer color (CSS animation overrides inline backgroundColor
  //     during its window).
  //   - Per-row zebra tint at 6%/3% opacity (Users page convention,
  //     matches users.component._row_tint_style) so the operator can
  //     pattern-match on color across the whole grid.
  //   - Stronger tint at 18% on rows stamped after the arrival snapshot,
  //     overriding the zebra so "new since you last looked" reads as
  //     visibly louder than the baseline color band.
  rowStyleForNew = (params: any) => {
    const slug = params?.data?.customer;
    const style: any = { '--row-flash-color': this.customerColors.flashRgb(slug) };
    if (this._isNewSinceArrival(params?.data)) {
      style.backgroundColor = this.customerColors.newRowTint(slug, 0.18);
    } else {
      const zebra = this.customerColors.rowZebraTint(slug, params?.node?.rowIndex || 0);
      if (zebra) {
        style.backgroundColor = zebra;
      } else {
        // Row's `customer` doesn't resolve to a Customer in the directory
        // (empty, `(unknown)`, deleted, or a slug the backend stamped
        // before it could figure out the real one — e.g. an unhandled
        // exception ER that crashed pre-resolve). Grey it out so the
        // operator can visually skip these when scanning for customer-
        // owned issues. Uses the same 6%/3% alternating opacity the
        // zebra tint does so scanning rhythm stays consistent.
        const opacityHex = (params?.node?.rowIndex || 0) % 2 === 0 ? '10' : '08';
        style.backgroundColor = `#6b7280${opacityHex}`;  // gray-500
        style.color = '#9ca3af';                          // gray-400 — dim the text a touch
      }
    }
    return style;
  };

  /** Mute toggle for the per-customer alert sound — persisted in localStorage by the service. */
  toggleSoundMute() {
    this.soundPlayer.setMuted(!this.soundPlayer.isMuted());
  }

  /** Volume slider handler. The value arrives as a string from the
   *  <input type="range">; coerce to number + delegate to the service
   *  which clamps to [0,100] + persists. */
  onVolumeChange(v: any) {
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    this.soundPlayer.setVolume(n);
  }

  /** Whether the per-customer Sound Settings dialog is open. Toggled by
   *  the toolbar Settings button; the dialog emits `close` to dismiss. */
  showSoundSettings = false;
  openSoundSettings() { this.showSoundSettings = true; }
  closeSoundSettings() { this.showSoundSettings = false; }

  async ngOnInit() {
    // Restore Live-on preference. Default off so a fresh operator sees
    // a static snapshot first and opts in to realtime.
    try {
      this.liveOn = localStorage.getItem(LIVE_LOCALSTORAGE_KEY) === '1';
    } catch { /* localStorage unavailable — leave default */ }

    // Deep-link: /admin/error-reports/:id can prefill the lookup.
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.lookupId = id;
      await this.onLookup();
    }
    await this.loadList();
    if (this.liveOn) this._open_event_source();
    this._start_when_refresh();
  }

  // Burst-breaker — drop incoming SSE events when rate exceeds threshold
  // and queue ONE full reload after the storm subsides. Protects the
  // browser from any future code path that floods change-stream events
  // (e.g. an upstream burst error-report writer that retries 1000s of
  // times during a backend outage).
  private _sseBurstBreaker = new SseBurstBreaker({
    onPostStormRefresh: () => this.loadList(),
  });

  ngOnDestroy() {
    this._close_event_source();
    this._sseBurstBreaker.dispose();
    if (this._whenRefreshTimer) {
      clearTimeout(this._whenRefreshTimer);
      clearInterval(this._whenRefreshTimer);
      this._whenRefreshTimer = null;
    }
  }

  // Tick the "When" column at every wall-clock minute boundary so
  // every visible row's relative timestamp ("5m ago", "1h ago") updates
  // in lockstep. ag-grid.refreshCells with `columns + force` only
  // re-renders the one column's cells — cheap even at LIVE_MAX_ROWS.
  // Runs outside the Angular zone so the heartbeat doesn't trigger
  // change detection on the rest of the page.
  private _start_when_refresh() {
    const tick = () => {
      const api = this.grid?.api;
      if (api?.refreshCells) {
        api.refreshCells({ columns: ['created_at'], force: true });
      }
    };
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    this.zone.runOutsideAngular(() => {
      this._whenRefreshTimer = setTimeout(() => {
        tick();
        this._whenRefreshTimer = setInterval(tick, 60_000);
      }, msUntilNextMinute);
    });
  }

  // Debounce timer for filter changes — clears the queued re-fetch when a
  // fast typer adds more chars so we only hit the backend after they stop.
  private _filterChangedDebounce: any = null;

  /**
   * Translate ag-grid's floating-filter input for every server-side
   * filterable column into an actual backend query via loadList(). Resets
   * the page stack on new filter so paging starts fresh from page 1 of
   * the filtered set.
   *
   * ag-grid filter model shapes:
   *   agTextColumnFilter:   { filter: <str>, type: 'contains'|'startsWith'|'equals'|... }
   *   agNumberColumnFilter: { filter: <num>, type: 'equals'|'lessThan'|'greaterThan'|... }
   *   agDateColumnFilter:   { dateFrom: <ISO>, dateTo: <ISO>, type: 'inRange'|'lessThan'|... }
   *
   * Translation rules (so an operator can find a row that lives beyond
   * the loaded 1000-row window):
   *   - text columns → case-insensitive contains regex server-side
   *     (server ignores ag-grid's startsWith/endsWith subtype; the regex
   *     already covers every case-insensitive match)
   *   - http_status → numeric op (eq / lt / gt / lte / gte / ne)
   *   - created_at → after (gte) + before (lt) bounds
   */
  private onGridFilterChanged(e: any) {
    if (this._filterChangedDebounce) clearTimeout(this._filterChangedDebounce);
    this._filterChangedDebounce = setTimeout(() => {
      this.zone.run(async () => {
        const model = e?.api?.getFilterModel?.() || {};
        // error_code column now uses agSetColumnFilter — shape is
        // {filterType:'set', values:[...]} (vs the old text-filter
        // {filter, type}). Handle both so a future swap back doesn't
        // need component edits.
        const ecModel = model.error_code || {};
        const ecNew = String(ecModel?.filter || '').trim();
        const ecInNew: string[] = Array.isArray(ecModel?.values)
          ? ecModel.values.map((v: any) => String(v || '').trim()).filter(Boolean)
          : [];
        const cNew = String(model.customer?.filter || '').trim();
        const idNew = String(model._id?.filter || '').trim();
        const pathNew = String(model.scanner_path?.filter || '').trim();
        const msgNew = String(model.f2_response_message?.filter || '').trim();
        const userNew = String(model.user_email?.filter || '').trim();

        const statusModel = model.http_status || null;
        let statusNum: number | null = null;
        let statusOp: 'eq' | 'lt' | 'gt' | 'lte' | 'gte' | 'ne' = 'eq';
        if (statusModel && statusModel.filter != null && statusModel.filter !== '') {
          const n = Number(statusModel.filter);
          if (Number.isFinite(n)) {
            statusNum = n;
            const t = String(statusModel.type || '');
            statusOp = t === 'lessThan' ? 'lt'
              : t === 'lessThanOrEqual' ? 'lte'
              : t === 'greaterThan' ? 'gt'
              : t === 'greaterThanOrEqual' ? 'gte'
              : t === 'notEqual' ? 'ne'
              : 'eq';
          }
        }

        const dateModel = model.created_at || null;
        let afterNew = '';
        let beforeFilterNew = '';
        if (dateModel) {
          const from = dateModel.dateFrom ? new Date(dateModel.dateFrom) : null;
          const to = dateModel.dateTo ? new Date(dateModel.dateTo) : null;
          const t = String(dateModel.type || '');
          if (t === 'inRange') {
            if (from && !isNaN(from.getTime())) afterNew = from.toISOString();
            if (to && !isNaN(to.getTime())) beforeFilterNew = to.toISOString();
          } else if (t === 'greaterThan' || t === 'greaterThanOrEqual') {
            if (from && !isNaN(from.getTime())) afterNew = from.toISOString();
          } else if (t === 'lessThan' || t === 'lessThanOrEqual') {
            if (from && !isNaN(from.getTime())) beforeFilterNew = from.toISOString();
          } else if (t === 'equals' && from && !isNaN(from.getTime())) {
            // "on this day" → 24h window
            const day = new Date(from);
            day.setUTCHours(0, 0, 0, 0);
            const next = new Date(day);
            next.setUTCDate(next.getUTCDate() + 1);
            afterNew = day.toISOString();
            beforeFilterNew = next.toISOString();
          }
        }

        const ecInChanged = ecInNew.length !== this.filterErrorCodesIn.length ||
          ecInNew.some((v, i) => v !== this.filterErrorCodesIn[i]);
        const unchanged =
          ecNew === this.filterErrorCode &&
          !ecInChanged &&
          cNew === this.filterCustomer &&
          idNew === this.filterId &&
          pathNew === this.filterScannerPath &&
          msgNew === this.filterMessage &&
          userNew === this.filterUserEmail &&
          statusNum === this.filterHttpStatus &&
          statusOp === this.filterHttpStatusOp &&
          afterNew === this.filterCreatedAfter &&
          beforeFilterNew === this.filterCreatedBefore;
        if (unchanged) return;

        this.filterErrorCode = ecNew;
        this.filterErrorCodesIn = ecInNew;
        this.filterCustomer = cNew;
        this.filterId = idNew;
        this.filterScannerPath = pathNew;
        this.filterMessage = msgNew;
        this.filterUserEmail = userNew;
        this.filterHttpStatus = statusNum;
        this.filterHttpStatusOp = statusOp;
        this.filterCreatedAfter = afterNew;
        this.filterCreatedBefore = beforeFilterNew;

        // New server query → reset pagination + suspend live so the new
        // page-1 of the filtered results is what's shown.
        this.pageStack = [];
        this._suspend_live(false);
        await this.loadList();
        if (this.liveOn) this._open_event_source();
      });
    }, 300);
  }

  /** Build the query string for /admin/error-reports list/page calls. */
  private _buildListParams(before?: string): string[] {
    const params: string[] = [`limit=${PAGE_SIZE}`];
    if (before) params.push('before=' + encodeURIComponent(before));
    else if (this.filterCreatedBefore) params.push('before=' + encodeURIComponent(this.filterCreatedBefore));
    if (this.filterCreatedAfter) params.push('after=' + encodeURIComponent(this.filterCreatedAfter));
    if (this.filterErrorCode.trim()) params.push('error_code=' + encodeURIComponent(this.filterErrorCode.trim()));
    if (this.filterErrorCodesIn.length > 0) {
      params.push('error_codes_in=' + encodeURIComponent(this.filterErrorCodesIn.join(',')));
    }
    if (this.filterCustomer.trim()) params.push('customer=' + encodeURIComponent(this.filterCustomer.trim()));
    if (this.filterId.trim()) params.push('id=' + encodeURIComponent(this.filterId.trim()));
    if (this.filterScannerPath.trim()) params.push('scanner_path=' + encodeURIComponent(this.filterScannerPath.trim()));
    if (this.filterMessage.trim()) params.push('message=' + encodeURIComponent(this.filterMessage.trim()));
    if (this.filterUserEmail.trim()) params.push('user_email=' + encodeURIComponent(this.filterUserEmail.trim()));
    if (this.filterHttpStatus != null) {
      params.push('http_status=' + this.filterHttpStatus);
      params.push('http_status_op=' + this.filterHttpStatusOp);
    }
    return params;
  }

  /**
   * Resolve a user email from a row's `extra` payload. Mirrors the
   * User column's valueGetter so a right-click on the cell agrees with
   * what the cell renders.
   */
  private _rowUserEmail(row: any): string {
    const e = row?.extra || {};
    return String(e.user_email || e.email || e.token_email || e.email_attempted || '').trim();
  }

  /**
   * Build ag-grid context-menu items for a right-clicked row. Returns
   * the two user-pivot actions (filter / lookup) when the row carries a
   * resolvable email, plus standard copy/export items so the right-click
   * still does the routine things.
   */
  private _buildContextMenu(params: any): any[] {
    const items: any[] = [];

    // Column-scoped action: right-click on the Error code cell → filter
    // the grid by that value. Same set-filter pipeline the header
    // dropdown uses; onFilterChanged fires the server refresh. Mike
    // 2026-07-07.
    const colId = params?.column?.getColId?.();
    const errorCode = String(params?.node?.data?.error_code || '').trim();
    if (colId === 'error_code' && errorCode) {
      items.push({
        name: `Filter reports by ${this._htmlEscape(errorCode)}`,
        icon: '<span style="color:#60a5fa">⚲</span>',
        action: () => this.zone.run(async () => {
          const api = params?.api;
          if (!api) return;
          try {
            await Promise.resolve(api.setColumnFilterModel('error_code', {
              filterType: 'set', values: [errorCode],
            }));
            api.onFilterChanged();
          } catch (err) {
            console.warn('Filter-by-error-code action failed', err);
          }
        }),
      });
      items.push('separator');
    }

    const email = this._rowUserEmail(params?.node?.data);
    if (email) {
      items.push({
        name: `Filter reports by ${this._htmlEscape(email)}`,
        icon: '<span style="color:#60a5fa">⚲</span>',
        action: () => this.zone.run(async () => {
          // Drive the existing server-side filter pipeline. Setting the
          // ag-grid filter model fires onFilterChanged → onGridFilterChanged
          // → resets paging + hits the backend with user_email=. Reaches
          // past the 1000-row window same as if the operator had typed.
          const api = params?.api;
          if (!api) return;
          try {
            await Promise.resolve(api.setColumnFilterModel('user_email', {
              filterType: 'text', type: 'contains', filter: email,
            }));
            api.onFilterChanged();
          } catch (err) {
            console.warn('Filter-by-user action failed', err);
          }
        }),
      });
      items.push({
        name: `Look up ${this._htmlEscape(email)} in Users panel`,
        icon: '<span style="color:#60a5fa">↗</span>',
        action: () => {
          // New tab so we don't lose the current grid state /
          // selection / live-mode position. Users component reads
          // ?email= and applies it to the email column.
          window.open('/admin/users?email=' + encodeURIComponent(email), '_blank');
        },
      });
      items.push('separator');
    }
    // Standard items so right-click still does the routine things.
    items.push('copy', 'copyWithHeaders', 'separator', 'export');
    return items;
  }

  /** Minimal HTML-escape for menu labels (the email is operator input
   * range but no reason to trust it for innerHTML interpolation). */
  private _htmlEscape(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Snapshot the ag-grid filter model, run a fetch, then restore the
   * snapshot. Belt-and-suspenders against the column-filter floating
   * inputs going blank after a server refresh — even with the grid
   * mounted across reloads (b508253c), ag-grid was clearing the model
   * when [rowData] flipped to a new array reference (Mike 2026-06-26).
   * Saving + restoring guarantees the user's typed filter survives
   * any internal reset. Safe to re-apply the same model because
   * onGridFilterChanged short-circuits when the parsed filter state
   * matches what's already on the component.
   */
  private _snapshotFilterModel(): any {
    try { return this.grid?.api?.getFilterModel?.() || null; }
    catch { return null; }
  }
  private _restoreFilterModel(snapshot: any) {
    if (!snapshot || !this.grid?.api?.setFilterModel) return;
    // Defer one microtask so ag-grid's own rowData-change handlers
    // finish first; otherwise our setFilterModel can race them and the
    // restore gets thrown out.
    queueMicrotask(() => {
      try { this.grid.api.setFilterModel(snapshot); } catch { /* grid torn down */ }
    });
  }

  /**
   * Bulk-delete the selected error reports. Confirms with the operator,
   * POSTs to /admin/error-reports/delete, then removes the rows from the
   * grid in-place (ag-grid applyTransaction). Selection clears as part
   * of the remove. 30-day TTL still catches anything we miss.
   */
  async deleteSelected() {
    if (this.deletingSelected) return;
    const api = this.grid?.api;
    if (!api) return;
    const selected = (api.getSelectedRows?.() || []) as ErrorReport[];
    if (!selected.length) return;
    const ids = selected.map((r) => r._id).filter(Boolean);
    if (!ids.length) return;
    const msg = ids.length === 1
      ? `Delete this 1 error report permanently? This cannot be undone.`
      : `Delete these ${ids.length} error reports permanently? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    this.deletingSelected = true;
    try {
      const url = `${this.apiBase}/admin/error-reports/delete`;
      const res: any = await this.http.post(url, { ids }, { withCredentials: true }).toPromise();
      const deleted = Number(res?.deleted ?? 0);
      // Patch grid: remove the affected rows from the in-memory list +
      // ag-grid transaction. getRowId is _id so we can build remove[]
      // directly from the selected list.
      api.applyTransaction?.({ remove: selected });
      this.list = this.list.filter((r) => !ids.includes(r._id));
      this.selectedCount = 0;
      // Light feedback — no toast service here, just a console line.
      // The grid visibly shrinking is the primary signal.
      console.info(`error-reports: deleted ${deleted} of ${ids.length} requested`);
    } catch (e: any) {
      const msg = e?.error?.message || e?.message || 'delete failed';
      window.alert(`Delete failed: ${msg}`);
    } finally {
      this.deletingSelected = false;
    }
  }

  async loadList() {
    const filterSnapshot = this._snapshotFilterModel();
    this.loadingList = true;
    this.listErr = null;
    try {
      const url = `${this.apiBase}/admin/error-reports?${this._buildListParams().join('&')}`;
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      this.list = Array.isArray(res) ? res : [];
      this.hasOlder = this.list.length === PAGE_SIZE;
    } catch (e: any) {
      this.listErr = e?.error?.message || e?.message || 'load failed';
      this.list = [];
      this.hasOlder = false;
    } finally {
      this.loadingList = false;
      this.hasInitialized = true;
      this._restoreFilterModel(filterSnapshot);
    }
  }

  // ── Paging ───────────────────────────────────────────────────────────
  // Fetch one window with a `before` cursor. Used by pageOlder/Newer.
  private async _loadPage(before?: string) {
    const filterSnapshot = this._snapshotFilterModel();
    this.loadingList = true;
    this.listErr = null;
    try {
      const url = `${this.apiBase}/admin/error-reports?${this._buildListParams(before).join('&')}`;
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      this.list = Array.isArray(res) ? res : [];
      this.hasOlder = this.list.length === PAGE_SIZE;
    } catch (e: any) {
      this.listErr = e?.error?.message || e?.message || 'load failed';
      this.list = [];
      this.hasOlder = false;
    } finally {
      this.loadingList = false;
      this.hasInitialized = true;
      this._restoreFilterModel(filterSnapshot);
    }
  }

  async pageOlder() {
    if (!this.list.length) return;
    // Cursor is the OLDEST visible row's created_at — the API returns
    // strictly older than that next.
    const oldest = this.list[this.list.length - 1]?.created_at;
    if (!oldest) return;
    this.pageStack.push(oldest);
    this._suspend_live(true);
    await this._loadPage(oldest);
  }

  async pageNewer() {
    if (this.pageStack.length === 0) return;
    // Pop the cursor that brought us here; re-fetch using the stack
    // top (the one above), or undefined if we're back at page 1.
    this.pageStack.pop();
    const before = this.pageStack.length ? this.pageStack[this.pageStack.length - 1] : undefined;
    if (this.pageStack.length === 0) {
      // Back to page 1 — un-suspend live + reload via the regular path
      // so the same default state applies.
      this._suspend_live(false);
      await this.loadList();
      if (this.liveOn) this._open_event_source();
    } else {
      await this._loadPage(before);
    }
  }

  async returnToNewest() {
    this.pageStack = [];
    this._suspend_live(false);
    await this.loadList();
    if (this.liveOn) this._open_event_source();
  }

  /**
   * True when any server-side filter field is set. Drives the Clear
   * Filters button's red-highlight state so operators can see at a
   * glance that a narrow set is in effect. Includes the ag-grid
   * floating-filter model as a belt-and-suspenders check — filters
   * live on both this component's state (server-side) AND ag-grid's
   * model (client-side narrow); either counts as "active".
   */
  hasActiveFilters(): boolean {
    if (
      this.filterErrorCode.trim() ||
      (this.filterErrorCodesIn && this.filterErrorCodesIn.length > 0) ||
      this.filterCustomer.trim() ||
      this.filterId.trim() ||
      this.filterScannerPath.trim() ||
      this.filterMessage.trim() ||
      this.filterUserEmail.trim() ||
      this.filterHttpStatus !== null ||
      this.filterCreatedAfter ||
      this.filterCreatedBefore
    ) return true;
    // Grid-side filter model (e.g. non-server-syncable columns).
    try {
      const model = this.grid?.api?.getFilterModel?.() || {};
      if (Object.keys(model).length > 0) return true;
    } catch { /* grid not ready */ }
    return false;
  }

  /**
   * Reset every filter — the component's server-side state AND ag-grid's
   * floating-filter model — then reload page 1 of the unfiltered result
   * set. Also un-suspends live so a filtered-history-then-clear returns
   * to real-time. Mike 2026-07-02: "error reports needs a Clear Filters
   * button that turns red when there are filters".
   */
  async clearAllFilters() {
    this.filterErrorCode = '';
    this.filterErrorCodesIn = [];
    this.filterCustomer = '';
    this.filterId = '';
    this.filterScannerPath = '';
    this.filterMessage = '';
    this.filterUserEmail = '';
    this.filterHttpStatus = null;
    this.filterHttpStatusOp = 'eq';
    this.filterCreatedAfter = '';
    this.filterCreatedBefore = '';
    try { this.grid?.api?.setFilterModel?.(null); } catch { /* grid not ready */ }
    this.pageStack = [];
    this._suspend_live(false);
    await this.loadList();
    if (this.liveOn) this._open_event_source();
  }

  // ── Live toggle ──────────────────────────────────────────────────────
  toggleLive() {
    this.liveOn = !this.liveOn;
    try { localStorage.setItem(LIVE_LOCALSTORAGE_KEY, this.liveOn ? '1' : '0'); } catch { /* ignore */ }
    if (this.liveOn && !this.liveSuspended) {
      this._open_event_source();
    } else {
      this._close_event_source();
    }
  }

  private _suspend_live(suspend: boolean) {
    if (this.liveSuspended === suspend) return;
    this.liveSuspended = suspend;
    if (suspend) {
      this._close_event_source();
    }
  }

  // Open an EventSource against /admin/error-reports/stream. Cross-origin
  // EventSource cookie delivery is unreliable across browsers, so auth is
  // a short-lived HMAC bearer minted by POST /stream-token (cookie-authed)
  // and passed on the query string. On error/reconnect we mint a fresh
  // token rather than letting EventSource's built-in retry loop against a
  // stale one — server TTL is 60s.
  private async _open_event_source() {
    if (this._eventSource) return; // already open
    if (this._reconnectTimer) return; // reconnect already scheduled
    let token: string;
    try {
      const mintUrl = `${this.apiBase}/admin/error-reports/stream-token`;
      const res: any = await this.http.post(mintUrl, {}, { withCredentials: true }).toPromise();
      token = res?.token;
      if (!token) throw new Error('mint response missing token');
    } catch (e: any) {
      console.warn('error-reports stream-token mint failed:', e?.message);
      this._schedule_sse_reconnect();
      return;
    }
    // Operator may have toggled Live off (or paged off page 1) while the
    // mint was in flight — don't open an EventSource they can no longer
    // see the indicator for.
    if (!this.liveOn || this.liveSuspended) return;
    const url = `${this.apiBase}/admin/error-reports/stream?token=${encodeURIComponent(token)}`;
    try {
      // No withCredentials — auth rides in the query token; cookies aren't
      // sent on EventSource handshake cross-browser-consistently anyway.
      const es = new EventSource(url);
      this._eventSource = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        // Burst-breaker check before parse; drops + queues one refresh
        // when inbound rate exceeds threshold.
        if (this._sseBurstBreaker.shouldDrop()) return;
        try {
          const doc = JSON.parse(ev.data);
          // ag-grid lives outside Angular's change-detection by default;
          // wrap the apply so detection fires for the banner / button
          // states that depend on `this.list.length`.
          this.zone.run(() => this._apply_live_doc(doc));
        } catch {
          /* malformed event; ignore */
        }
      };
      es.onerror = () => {
        // EventSource's built-in auto-reconnect would loop with the same
        // (now likely expired) token forever. Close + re-mint instead.
        console.warn('error-reports stream error; will mint fresh token + reopen');
        this._close_event_source();
        this._schedule_sse_reconnect();
      };
    } catch (e: any) {
      console.warn('error-reports stream open failed:', e?.message);
      this._eventSource = null;
      this._schedule_sse_reconnect();
    }
  }

  private _schedule_sse_reconnect() {
    if (this._reconnectTimer) return;
    if (!this.liveOn || this.liveSuspended) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this.liveOn && !this.liveSuspended) {
        this._open_event_source();
      }
    }, 3000);
  }

  private _close_event_source() {
    if (this._eventSource) {
      try { this._eventSource.close(); } catch { /* ignore */ }
      this._eventSource = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  // Apply a live-arrived doc to the grid: prepend, mark fresh for
  // 2 seconds (rowClassRules picks it up), cap at LIVE_MAX_ROWS.
  private _apply_live_doc(doc: ErrorReport) {
    if (!doc || !doc._id) return;
    // Skip duplicates — the change stream is at-least-once on resume,
    // and ag-grid's getRowId would also reject duplicate ids loudly.
    if (this.list.some(r => r._id === doc._id)) return;

    // Per-customer alert sound. Fire-and-forget — service no-ops when
    // muted / no config / browser autoplay-blocked. Different customers
    // pick different notes so operators can identify by sound alone.
    this.soundPlayer.playForCustomer((doc as any)?.customer);

    doc._freshUntil = Date.now() + LIVE_HIGHLIGHT_MS;
    // Prepend to the underlying array AND the grid transaction so both
    // the data model and ag-grid's view are kept in lockstep. ag-grid
    // honors `getRowId` so the transaction add doesn't re-render rows
    // that haven't changed.
    this.list = [doc, ...this.list];

    const api = this.grid?.api;
    if (api?.applyTransaction) {
      api.applyTransaction({ add: [doc], addIndex: 0 });
    }

    // Cap from the tail — both array and ag-grid stay in lockstep.
    if (this.list.length > LIVE_MAX_ROWS) {
      const overflow = this.list.length - LIVE_MAX_ROWS;
      const dropped = this.list.slice(this.list.length - overflow);
      this.list = this.list.slice(0, LIVE_MAX_ROWS);
      if (api?.applyTransaction) {
        api.applyTransaction({ remove: dropped });
      }
    }

    // Schedule a redraw at the moment the highlight expires so the
    // rowClassRules predicate is re-evaluated and the class drops.
    // Cheap: only the one row.
    setTimeout(() => {
      const rowNode = api?.getRowNode?.(doc._id);
      if (rowNode && api?.redrawRows) {
        api.redrawRows({ rowNodes: [rowNode] });
      }
    }, LIVE_HIGHLIGHT_MS + 50);
  }

  // Lookup-by-ID: takes whatever the operator pasted, trims it,
  // normalizes ("er-7m4qpk3" → "ER-7M4QPK3"), and fetches.
  async onLookup() {
    this.lookupErr = null;
    this.selected = null;
    const raw = (this.lookupId || '').trim().toUpperCase();
    if (!raw) {
      this.lookupErr = 'Paste a support ID to look up.';
      return;
    }
    // Tolerate the operator pasting "Support ID: ER-XXXXXXX" or a full
    // sentence — extract the first ER-... fragment.
    const m = raw.match(/ER-[A-Z0-9]{4,}/);
    const id = m ? m[0] : raw;
    this.loadingLookup = true;
    try {
      const url = `${this.apiBase}/admin/error-reports/${encodeURIComponent(id)}`;
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      this.selected = res;
    } catch (e: any) {
      if (e?.status === 404) {
        this.lookupErr = `No report found for ${id}. Records auto-expire after 30 days.`;
      } else {
        this.lookupErr = e?.error?.message || e?.message || 'lookup failed';
      }
    } finally {
      this.loadingLookup = false;
    }
  }

  async onSelect(report: ErrorReport) {
    this.lookupId = report._id;
    this.lookupErr = null;
    this.selected = report;
    // Fresh row -> clear any prior WC on-demand fetch state so the
    // panel doesn't show a previous report's payload while ops browses.
    this.wcFetchResult = null;
    this.wcFetchError = null;
    this.wcFetching = false;
  }

  // ----- WooCommerce on-demand payload fetch -----
  // Stamped on every WC-topic ER emitted 2026-07-09+ via
  // extra.wc_subscription_lookup_url. When ops clicks the Fetch button
  // we call that URL (same-origin via the Vercel rewrite chain) and
  // render the returned sub JSON in a scrollable pane. On-demand
  // instead of always-stored: WC's REST API is the authoritative
  // source; storing multi-KB copies of every webhook body forever was
  // wasteful (retired the wc_payload_full snapshot the same day).
  wcFetching = false;
  wcFetchResult: any = null;
  wcFetchError: string | null = null;
  async fetchWcSubscription(report: ErrorReport): Promise<void> {
    const rawUrl = report?.extra?.['wc_subscription_lookup_url'];
    if (!rawUrl) return;
    this.wcFetching = true;
    this.wcFetchError = null;
    this.wcFetchResult = null;
    try {
      // The stamped URL is absolute (built by the service). Strip the
      // scheme+host so the fetch rides the SPA's same-origin Vercel
      // rewrites -- avoids the Firefox 152 credentialed cross-origin
      // POST-abort quirk documented in the F2 env.apiUrl comment.
      const path = rawUrl.replace(/^https?:\/\/[^/]+/, '');
      const res: any = await firstValueFrom(this.http.get(path, { withCredentials: true }));
      if (res?.ok === false || res?.err) {
        this.wcFetchError = String(res.err || `WC returned ${res.http_status || 'error'}`);
        return;
      }
      this.wcFetchResult = res?.subscription || res;
    } catch (e: any) {
      this.wcFetchError = e?.message || 'Fetch failed';
    } finally {
      this.wcFetching = false;
    }
  }

  prettyJson(o: any): string {
    try { return JSON.stringify(o, null, 2); }
    catch { return String(o); }
  }

  /**
   * Absolute timestamp for the details header (Mike 2026-06-26 — the
   * relative "2m ago" alone was missing wall-clock context). Forced to
   * ET so every operator reads the same value regardless of their
   * browser timezone.
   */
  formatDetailWhen(iso: string): string {
    if (!iso) return '—';
    const dt = DateTime.fromISO(iso).setZone('America/New_York');
    if (!dt.isValid) return iso;
    return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
  }

  /**
   * Full wall-clock timestamp for the "When" column's hover tooltip.
   * The column body shows "5m ago" so the operator can scan freshness
   * at a glance; the tooltip resolves that to a real day/date/time in
   * ET (so every operator reads the same value regardless of their
   * browser timezone) plus a UTC line for cross-referencing logs.
   * Mike 2026-07-06.
   */
  formatFullWhen(iso: string): string {
    if (!iso) return '—';
    const dt = DateTime.fromISO(iso).setZone('America/New_York');
    if (!dt.isValid) return iso;
    const et = dt.toFormat('cccc, LLLL d, yyyy hh:mm:ss a ZZZZ');
    const utc = dt.toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z' 'UTC'");
    return `${et}\n${utc}`;
  }

  /**
   * Resolve the User email for the details header — mirrors the
   * grid's user_email valueGetter so the two displays stay in sync.
   * Falls through extra.user_email → email → token_email →
   * email_attempted (any handler-specific key) so every code path
   * that carries an identity surfaces it here.
   */
  detailUserEmail(report: any): string {
    const e = report?.extra || {};
    return e.user_email || e.email || e.token_email || e.email_attempted || '';
  }

  formatRelative(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return Math.floor(diff) + 's ago';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return d.toISOString().slice(0, 16).replace('T', ' ');
  }

  copyText(s: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(s).catch(() => {});
    }
  }

  // Transient — drives the "Copied" swap on the two per-section copy
  // buttons in the details pane. Reverts after 1.5s. Keyed by section
  // id ('resp' | 'extra') so each button flashes independently.
  copied: string | null = null;
  private _copiedTimer: any = null;
  flashCopy(key: string): void {
    this.copied = key;
    if (this._copiedTimer) clearTimeout(this._copiedTimer);
    this._copiedTimer = setTimeout(() => {
      this.copied = null;
      this._copiedTimer = null;
    }, 1500);
  }

  // Build the shareable deep-link URL to the currently-selected report
  // (or a passed-in id). Uses the SPA's own origin so the URL works
  // whichever brand-host the operator is on.
  linkFor(id: string): string {
    if (typeof window === 'undefined') return `/admin/error-reports/${id}`;
    return `${window.location.origin}/admin/error-reports/${id}`;
  }

  copyLink(id: string) {
    this.copyText(this.linkFor(id));
  }

  clearSelection() {
    this.selected = null;
    this.lookupId = '';
    this.lookupErr = null;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.grid?.api?.sizeColumnsToFit?.();
  }

  // When the operator has rows checkbox-selected, copy buttons operate
  // on those; otherwise on the visible-after-filter rows. Mike's spec.
  private getEffectiveRows(): ErrorReport[] {
    const api = this.grid?.api;
    if (!api) return [];
    const selected = api.getSelectedRows?.() as ErrorReport[] | undefined;
    if (selected && selected.length > 0) return selected;
    const rows: ErrorReport[] = [];
    api.forEachNodeAfterFilterAndSort((n: any) => {
      if (n?.data) rows.push(n.data as ErrorReport);
    });
    return rows;
  }

  effectiveCount(): number {
    if (this.selectedCount > 0) return this.selectedCount;
    return this.list.length;
  }

  // Button label suffix: "(N selected)" when there are checked rows,
  // "(N visible)" otherwise.
  copyButtonLabel(prefix: string): string {
    const n = this.effectiveCount();
    if (n === 0) return prefix;
    const noun = this.selectedCount > 0 ? 'selected' : 'visible';
    return `${prefix} (${n} ${noun})`;
  }

  onCopyVisibleRowsJson() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    this.copyText(JSON.stringify(rows, null, 2));
  }

  onCopyVisibleRowsIds() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    this.copyText(rows.map(r => r._id).join(', '));
  }

  onCopyVisibleRowsCsv() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    const headers = ['ID', 'When', 'Error code', 'Customer', 'User', 'Path', 'Status', 'Message'];
    const esc = (v: unknown): string => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(',')];
    for (const r of rows) {
      lines.push([
        r._id,
        r.created_at,
        r.error_code,
        r.customer,
        r.extra?.['user_email'] || r.extra?.['email'] || r.extra?.['token_email'] || r.extra?.['email_attempted'] || '',
        r.scanner_path,
        r.http_status,
        r.f2_response_message,
      ].map(esc).join(','));
    }
    this.copyText(lines.join('\n'));
  }

  /**** Recent-reports grid (ag-grid) ****/
  init_grid() {
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: true,
          enableClickSelection: false,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        // rowClassRules drives the live-row teal pulse — predicate runs
        // on every redraw and clears the class when _freshUntil expires.
        rowClassRules: {
          'live-row-fresh': (params: any) => {
            const u = params?.data?._freshUntil;
            return typeof u === 'number' && Date.now() < u;
          },
        },
        // Per-row background tint for rows the operator hasn't seen yet.
        // Driven by UserPrefsService arrival snapshot + per-customer
        // accent_color from CustomerColorsService. Returns undefined for
        // already-seen rows so ag-grid's normal striping applies.
        getRowStyle: (params: any) => this.rowStyleForNew(params),
        getRowId: (params: any) => params.data._id,
        onRowClicked: (e: any) => { if (e?.data) this.onSelect(e.data); },
        onSelectionChanged: (e: any) => {
          this.zone.run(() => { this.selectedCount = e?.api?.getSelectedRows?.()?.length || 0; });
        },
        onGridReady: (e: any) => {
          this.grid.api = e.api;
          e.api.sizeColumnsToFit();
        },
        onFirstDataRendered: (e: any) => e.api.sizeColumnsToFit(),
        // Hook ag-grid's floating filter for the two server-side filterable
        // columns (error_code, customer) to drive an actual SERVER query.
        // Without this, those filters only narrow the loaded 100-row window
        // and operators trying to find an older report by code/customer get
        // empty results when their match is on page 2+. Other columns
        // (Path, Message, User, etc.) continue with pure client-side
        // narrowing on top of the loaded window. 300ms debounce so a fast
        // typer doesn't hammer the backend on every keystroke.
        onFilterChanged: (e: any) => this.onGridFilterChanged(e),
        // Right-click context menu — when an operator right-clicks a row
        // with a resolvable email (post-auth toast, login-failure toast,
        // JWT-decoded), offer two quick actions: narrow the current grid
        // to just that user's reports (drives the server-side
        // user_email filter so it reaches past the 1000-row window), or
        // pop the Users panel pre-filtered to that user so we can see
        // their pool, role, claims, status pill, etc. without typing.
        getContextMenuItems: (params: any) => this._buildContextMenu(params),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        ],
      },
      columnDefs: [
        // Tiny indicator column — empty for already-seen rows, renders
        // a NEW pill for rows stamped after the arrival snapshot. Pair
        // with getRowStyle's customer-color tint to give the operator
        // both a glyph and a color cue on what's new since last visit.
        {
          headerName: '',
          width: 50,
          suppressSizeToFit: true,
          sortable: false,
          filter: false,
          resizable: false,
          cellStyle: { 'text-align': 'center', 'padding': '0 4px' },
          cellRenderer: (params: any) => {
            if (!this._isNewSinceArrival(params?.data)) return '';
            return '<span style="display:inline-block;padding:1px 6px;border-radius:9999px;background:#dc2626;color:#fff;font-size:9px;font-weight:700;letter-spacing:.04em;line-height:14px;vertical-align:middle;">NEW</span>';
          },
        },
        {
          field: '_id',
          headerName: 'ID',
          width: 140,
          suppressSizeToFit: true,
          filter: 'agTextColumnFilter',
          cellClass: 'font-mono',
          cellStyle: { 'text-align': 'left', color: '#5eead4' },
          comparator: (a: any, b: any) => String(a ?? '').localeCompare(String(b ?? '')),
        },
        {
          field: 'created_at',
          // IT-F2-450 cid efadce69 — header renamed to "When (ET)" so
          // operators reading the exact timestamp see the tz up front.
          // Mike chat 2026-10-22.
          headerName: 'When (ET)',
          width: 140,
          suppressSizeToFit: true,
          filter: 'agDateColumnFilter',
          valueFormatter: (p: any) => this.formatRelative(p.value),
          // Cell shows "5m ago"; hover reveals the full ET wall-clock
          // date + time (plus a UTC line) so an operator triaging can
          // read the exact moment without opening the details pane.
          // Mike 2026-07-06.
          tooltipValueGetter: (p: any) => this.formatFullWhen(p.value),
          comparator: (a: any, b: any) => String(a ?? '').localeCompare(String(b ?? '')),
          // Default sort: NEWEST FIRST so the live stream's prepends
          // visually land at the top. Operators wanted "most recent
          // when you open it" per the live-mode redesign (2026-06-16).
          sort: 'desc' as any,
          sortIndex: 0,
        },
        {
          field: 'error_code',
          headerName: 'Error code',
          width: 200,
          // Set-filter dropdown populated from a server-side distinct
          // query (/admin/error-reports/distinct/error-codes) so the
          // operator picks from the actual possibility space instead of
          // typing a code. refreshValuesOnOpen re-fetches every time
          // the popup opens so newly-seen codes show up without a
          // grid reload. Mike 2026-06-26.
          filter: 'agSetColumnFilter',
          filterParams: {
            values: async (params: any) => {
              try {
                const res: any = await this.http.get<string[]>(
                  `${this.apiBase}/admin/error-reports/distinct/error-codes`,
                  { withCredentials: true },
                ).toPromise();
                params.success(Array.isArray(res) ? res : []);
              } catch {
                params.success([]);
              }
            },
            refreshValuesOnOpen: true,
            buttons: ['reset', 'apply'],
            closeOnApply: true,
          },
          cellClass: 'font-mono',
        },
        {
          field: 'customer',
          headerName: 'Customer',
          width: 140,
          filter: 'agTextColumnFilter',
          // Color-coded pill sourced from F2-ADMIN.Customers.brand_color
          // via CustomerColorsService — matches the Users grid Pool
          // column pattern so the two admin surfaces read consistently.
          // Slug string still appears in the cell (label = 'f2 (legacy)'
          // for the legacy pool) so ag-grid text filtering works on the
          // raw value — the innerHTML is purely visual.
          cellRenderer: (params: any) => {
            const slug = params?.value;
            if (!slug) return '<span class="text-gray-400">—</span>';
            // (unknown) means the backend crashed before it could resolve
            // the ER's customer — always a bug. Bright red pill so it
            // stands out immediately when scanning; purple fallback
            // (previous default) was too close to the many purple-branded
            // customers and buried the signal.
            const isUnknown = slug === '(unknown)';
            const color = isUnknown
              ? '#ef4444'  // red-500 — bright + high-contrast against dark grid
              : (this.customerColors.brandColorFor(slug) || (slug === 'f2' ? '#94a3b8' : '#7c3aed'));
            const label = slug === 'f2' ? 'F2 (legacy)' : slug;
            // (unknown) uses louder opacity (25% bg, 60% border) so the pill
            // reads as an alert, not another zebra-band color.
            const bgHex = isUnknown ? '40' : '1a';
            const borderHex = isUnknown ? '99' : '33';
            const style = `background:${color}${bgHex};color:${color};border:1px solid ${color}${borderHex}`;
            return `<span class="px-1.5 py-0.5 rounded text-[10px] font-mono" style="${style}">${label}</span>`;
          },
        },
        {
          // User column — error reports across different code paths put
          // the email under different keys in `extra`:
          //   - user_email      → signed-in user (post-auth toasts)
          //   - email           → generic email-carrying handler
          //   - token_email     → email decoded from a JWT
          //   - email_attempted → login-failure toasts (no session yet)
          // Falling back across all four covers every path that surfaces
          // an identity; null when the toast didn't carry one
          // (anonymous error paths). Hidden behind a valueGetter so the
          // SPA payload shape doesn't leak into the rest of the config.
          colId: 'user_email',
          headerName: 'User',
          width: 200,
          filter: 'agTextColumnFilter',
          valueGetter: (p: any) => {
            const e = p.data?.extra || {};
            return e.user_email || e.email || e.token_email || e.email_attempted || '';
          },
          valueFormatter: (p: any) => p.value || '—',
          cellClass: 'font-mono',
          tooltipValueGetter: (p: any) => {
            const e = p.data?.extra || {};
            return e.user_email || e.email || e.token_email || e.email_attempted || '';
          },
        },
        {
          field: 'scanner_path',
          headerName: 'Path',
          flex: 1,
          minWidth: 200,
          filter: 'agTextColumnFilter',
          cellClass: 'font-mono',
          tooltipField: 'scanner_path',
        },
        {
          field: 'http_status',
          headerName: 'Status',
          width: 100,
          suppressSizeToFit: true,
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'f2_response_message',
          headerName: 'Message',
          flex: 2,
          minWidth: 240,
          filter: 'agTextColumnFilter',
          tooltipField: 'f2_response_message',
        },
      ],
    };
  }
}
